import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { useHistory, useLocation } from 'react-router-dom';
import Item, { canRenderPreview } from './Item';
import GiftTile from './GiftTile';
import ItemSubtitle from './ItemSubtitle';
import { addonUnitPrice } from '../../../../utils/functions';
import GiftHero from './GiftHero';
import { PRODUCT_GRID_ID } from './giftHeroConfig';
import { ORDER_TYPES } from './orderType';
import { popularityComparator } from './popularityOrder';
import productKeyOf from './productKey';
import eligiblePoses from '../../../PackageBuilders/MultiPose/poseEligibility';
import styles from './HolidayBuilder.module.scss';

/**
 * Announcements & Gifts, as a shop.
 *
 * The page used to be one flat grid in whatever order the API returned — for a
 * senior that put "Coffee Cup" third, between two graduation announcements, and
 * a customer looking for one particular thing had no way to get to it but to
 * scroll. This is the ordinary storefront answer: filter down the left, search
 * and sort across the top, products in a grid.
 *
 * ⚠️ THE CATEGORIES COME FROM THE SERVER. Each add-on arrives carrying
 * `giftCategory` / `giftCategoryLabel` / `giftCategoryPosition`, resolved by
 * Addon#gift_category from the admin-set column. So the sidebar is whatever
 * /admin/addons says it is, and re-filing a product moves it with no deploy —
 * there is deliberately no css_name → section map in this file.
 */

// Newest first. `createdAt` is the ADD-ON ROW's creation date -- when the
// product was put on the shelf, which is the closest thing the data has to
// "arrived". It spreads across two years and eight distinct dates on the live
// shelf, so it orders meaningfully rather than collapsing to one import batch.
//
// A row with no date, or an unparseable one, sorts LAST rather than first:
// `new Date(undefined)` is NaN, every comparison with it is false, and left
// unguarded it would scatter dateless products through the list at whatever
// positions the sort happened to visit them in.
const arrivedAt = (item) => {
  const t = new Date(item.addon?.createdAt).getTime();

  return Number.isNaN(t) ? -Infinity : t;
};

/**
 * Sort options, in the order the control lists them. The FIRST key is the
 * default.
 *
 * "Custom order" is the default and means LEAVE THE SERVER ORDER ALONE. That
 * order is set by hand on /admin/addons -- dragging the rows on the Gifts tab
 * writes `addons.gifts_position`, and `Api::AddonResource` sorts by it -- so
 * what an admin drags to the top is what a shopper sees first. It deliberately
 * outranks category: the grid is flat, and an admin's order for the whole shelf
 * cannot survive being regrouped. Anyone who wants a different order picks one
 * from this control.
 *
 * "Most Popular" is the only one that depends on WHO is shopping -- see
 * popularityOrder.js, where each order type leads with something different. It
 * is a curated order rather than a sales figure; the file says so at length.
 *
 * `compare: null` marks the two the memo below handles specially (one needs the
 * order type, the other needs no sort at all); the price and date options are
 * plain comparators and the same for everybody.
 */
const SORTS = {
  custom: { label: 'Custom order', compare: null },
  mostPopular: { label: 'Most Popular', compare: null },
  newArrivals: { label: 'New Arrivals', compare: (a, b) => arrivedAt(b) - arrivedAt(a) },
  priceAsc: { label: 'Price: low to high', compare: (a, b) => a.unitPrice - b.unitPrice },
  priceDesc: { label: 'Price: high to low', compare: (a, b) => b.unitPrice - a.unitPrice },
};

// The default, taken from the table rather than written twice.
const DEFAULT_SORT = Object.keys(SORTS)[0];

const ALL = 'all';

// The two sort mounts, named so a test can say WHICH one it is driving. Both are
// in the DOM at every width and CSS hides one; jsdom has no media queries, so a
// bare getByLabelText('Sort') would match two elements and throw.
export const SORT_ROW_DESKTOP = 'gift-sort-row';
export const SORT_ROW_PHONE = 'gift-sort-row-phone';

/**
 * The sort control, written ONCE and mounted twice -- in the toolbar from `md`
 * up, and on its own row below it on a phone. Two copies of this markup would be
 * two places for the option list, the chevron, the `.form-control` avoidance and
 * the tap-target floor to drift apart, which is the bug this page has been bitten
 * by repeatedly (see the hero's decorative pair).
 *
 * `id` is a prop because both mounts are in the DOM at once and two elements
 * cannot share the id a <label htmlFor> points at.
 *
 * `compact` pins 40px for the toolbar, where the control sits on a 46/52px row.
 * Left OFF on the phone row, so that one keeps the page's 44px tap-target floor
 * -- a 40px target is under the 44px minimum on the device that needs it most.
 */
function SortControl({ id, sortKey, onChange, compact }) {
  return (
    <>
      <label className="text-[14px] font-semibold text-gift-inkSoft" htmlFor={id}>
        Sort
      </label>
      <select
        id={id}
        value={sortKey}
        onChange={(e) => onChange(e.target.value)}
        /* NOT `.form-control`: dprojects.scss forces that class to
           `width: 100% !important` app-wide, which would stretch this select
           across the whole row. */
        className={'cursor-pointer appearance-none rounded-gift-img border border-solid '
          + 'border-gift-lineStrong bg-white pl-3 pr-9 text-[14px] font-semibold text-gift-ink '
          + 'focus:border-gift-accent focus:outline-none'}
        /* ⚠️ minHeight, not just height. The page sets `min-height: 44px` on
           every select inside it (the tap-target floor from the design
           foundation), which silently wins over a plain 40px height and
           rendered this control at 44. */
        style={compact ? { ...SELECT_CHEVRON, height: '40px', minHeight: '40px' } : SELECT_CHEVRON}
      >
        {Object.entries(SORTS).map(([key, { label }]) => (
          <option key={key} value={key}>{label}</option>
        ))}
      </select>
    </>
  );
}

SortControl.propTypes = {
  id: PropTypes.string.isRequired,
  sortKey: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  compact: PropTypes.bool,
};

SortControl.defaultProps = { compact: false };

/**
 * The category chooser AS A SELECT, for the phone row only.
 *
 * ⚠️ THE TAB STRIP AND THIS ARE ONE CHOICE, not two. Both read `category` from
 * the url and both call `setCategory`, so there is no second piece of state to
 * fall out of step -- the strip is simply the wide-screen presentation of the
 * same control.
 *
 * A native <select> rather than a custom menu: it is one line, it is reachable
 * by keyboard for free, and on a phone the OS draws it as the picker the
 * customer already knows.
 */
function CategorySelect({ tabs, category, onChange }) {
  return (
    <>
      <label className="sr-only" htmlFor="gift-category">Category</label>
      <select
        id="gift-category"
        value={category}
        onChange={(e) => onChange(e.target.value)}
        /* NOT `.form-control`: dprojects.scss forces that class to
           `width: 100% !important` app-wide. */
        className={'min-w-0 flex-1 cursor-pointer appearance-none rounded-gift-img border border-solid '
          + 'border-gift-lineStrong bg-white pl-3 pr-9 text-[14px] font-semibold text-gift-ink '
          + 'focus:border-gift-accent focus:outline-none'}
        style={SELECT_CHEVRON}
      >
        <option value={ALL}>All categories</option>
        {tabs.map((tab) => (
          <option key={tab.key} value={tab.key}>{tab.label}</option>
        ))}
      </select>
    </>
  );
}

CategorySelect.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  tabs: PropTypes.array.isRequired,
  category: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

/**
 * The category tabs, in the order they are shown.
 *
 * ⚠️ NOT the server's `giftCategoryPosition`, which orders the sections
 * announcements → magnets → gifts → prints. This is the tab strip's own running
 * order, which the design puts Photo Gifts before Magnets.
 *
 * Only tabs with products FOR THIS CUSTOMER are drawn, and the labels come from
 * the data (`giftCategoryLabel`), not from this list — re-labelling a section on
 * /admin/addons still renames the tab with no deploy. A category present in the
 * data but missing from this list is appended rather than dropped: "All" would
 * still reach its products, but a tab strip that silently cannot reach a
 * product it is showing is worse than one with an extra tab.
 */
const TAB_ORDER = ['announcements', 'gifts', 'magnets', 'prints'];

/**
 * Shorter names for the tab strip, where the section's full label is too long
 * to sit in a row of tabs. ONLY an override: a category with no entry here uses
 * `giftCategoryLabel` from the data, so re-labelling a section on /admin/addons
 * still renames its tab with no deploy.
 *
 * "Graduation Announcements" is the section heading; on a tab beside four
 * others it is twice the width of anything next to it, and the page it sits on
 * is already headed "Announcements & Gifts".
 */
const TAB_LABEL_OVERRIDES = {
  announcements: 'Announcements',
};

const CATEGORY_PARAM = 'category';

// The sort control's chevron. A data URI because the select is `appearance:none`
// (a native arrow cannot be styled) and this keeps it a background image rather
// than an absolutely-positioned icon that would have to dodge the text.
const SELECT_CHEVRON = {
  backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' "
    + "height='8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23243040' stroke-width='2' fill='none' "
    + "stroke-linecap='round'/%3E%3C/svg%3E\")",
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 14px center',
};

export default function GiftShop({
  addons, onSelect, poses, backgrounds, orderPackage, offer, qualifiesForFreeAnnouncements,
  poseOrientations,
  inOrderFlow, orderType, addedAddons, onOpenAdded,
}) {
  const history = useHistory();
  const location = useLocation();
  const [sortKey, setSortKey] = useState(DEFAULT_SORT);

  // The tabs the data supports: TAB_ORDER's running order, narrowed to
  // categories this customer actually has products in, then anything the data
  // carries that TAB_ORDER does not name (see the note on TAB_ORDER).
  const tabs = useMemo(() => {
    const present = new Map();

    addons.forEach((addon) => {
      if (!addon.giftCategory || present.has(addon.giftCategory)) return;
      present.set(addon.giftCategory, TAB_LABEL_OVERRIDES[addon.giftCategory]
        || addon.giftCategoryLabel
        || addon.giftCategory);
    });

    const ordered = TAB_ORDER.filter((key) => present.has(key));
    const extras = [...present.keys()].filter((key) => !TAB_ORDER.includes(key));

    return [...ordered, ...extras].map((key) => ({ key, label: present.get(key) }));
  }, [addons]);

  // ⚠️ THE URL IS THE SOURCE OF TRUTH, not component state. Derived rather than
  // mirrored so Back and Forward simply work: the browser changes the URL, this
  // recomputes, and there is no second copy to fall out of step with it.
  const categoryParam = new URLSearchParams(location.search).get(CATEGORY_PARAM);

  // A category in the URL that this customer has no products in would show an
  // empty shelf -- from a shared link, or last season's bookmark. Fall back to
  // everything rather than to nothing.
  const category = tabs.some((tab) => tab.key === categoryParam) ? categoryParam : ALL;

  // Push, not replace: picking a tab is a place the customer can come Back to.
  // Every other param is carried across -- sch, sid, access_code, pid and
  // optional all have to survive, and dropping one would reset the session.
  const setCategory = useCallback((next) => {
    const params = new URLSearchParams(location.search);

    if (next === ALL) params.delete(CATEGORY_PARAM);
    else params.set(CATEGORY_PARAM, next);

    history.push(`${location.pathname}?${params.toString()}`);
  }, [history, location.pathname, location.search]);

  // Price is needed for sorting AND for the card, and addonUnitPrice depends on
  // the order package — resolve it once per add-on rather than in both places.
  const priced = useMemo(
    () => addons.map((addon) => ({ addon, unitPrice: Number(addonUnitPrice(addon, orderPackage)) || 0 })),
    [addons, orderPackage],
  );

  const visible = useMemo(() => {
    const matches = priced.filter(({ addon }) => category === ALL || addon.giftCategory === category);

    // The admin's own order, straight from the server -- no sort at all, not
    // even a stable no-op one, so the slot an add-on was dragged into is the
    // slot it renders in.
    if (sortKey === 'custom') return matches;

    // "Most Popular" is not "leave it alone": it reorders by order type. Its
    // comparator returns 0 for products the config has no opinion about, and
    // Array#sort is stable, so those keep the order the server sent.
    if (sortKey === 'mostPopular') {
      const comparePopularity = popularityComparator(orderType);

      return [...matches].sort((a, b) => comparePopularity(a.addon, b.addon));
    }

    return [...matches].sort(SORTS[sortKey].compare);
  }, [priced, category, sortKey, orderType]);

  return (
    /* FULL CONTENT WIDTH: 1440px cap, 64px side gutters. The 64px is desktop
       only -- at 64px a side on a 390px phone the grid would be left with 262px
       for two product tiles, so the gutter steps up with the viewport. */
    <div className="mx-auto max-w-[1440px] px-4 pb-24 md:px-10 lg:px-16">
      {/* BREADCRUMB IS CATALOG-ONLY AND OUT-OF-FLOW-ONLY. A customer who came
          here mid-order did not navigate to a shop: they were sent here by the
          package they just built, and "Home > Announcements & Gifts" describes a
          journey they did not take. Out of flow -- a campaign link, a direct
          visit -- it is the only orientation on the page, so it stays. */}
      {!inOrderFlow && (
        <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-2 pb-1 pt-4 text-sm text-gift-inkSoft">
          <span>Home</span>
          <span className="text-gift-line" aria-hidden="true">&rsaquo;</span>
          <span>Announcements &amp; Gifts</span>
        </nav>
      )}

      {/* The page title and subtitle used to sit here. Removed: the header above
          the page already names where the customer is, and on the in-flow entry
          the first thing on screen should be the products. */}

      <GiftHero
        orderType={orderType}
        addons={addons}
        poses={poses}
        poseOrientations={poseOrientations}
        backgrounds={backgrounds}
      />

      {/* ──────────────────────────────────────────────────────────── toolbar
          Category tabs left, sort right, on a 1px rule. The tabs replaced the
          old filter sidebar: same filtering, no column of the page spent on it.

          `items-end` so the active tab's 3px underline sits ON the row's rule
          rather than above it -- the tab's own -1px margin pulls it over the
          border, which is what makes the two read as one line. */}
      <div
        /* ⚠️ NO WRAPPING. With `flex-wrap` the sort control dropped to a second
           line at 768 — five tabs plus the select just exceed the row — which
           left the active tab's underline stranded 58px above the rule it is
           supposed to sit on. The strip scrolls instead (it already can), so
           the two stay on one line at every width. */
        /* ⚠️ WIDE SCREENS ONLY. On a phone the tab strip and the sort control
           cost two full rows above the products -- see the phone row below,
           which is the same two choices on one line. `max-md:hidden`, never a
           bare `hidden`, which Bootstrap declares `!important`. */
        className={'mt-8 flex flex-nowrap items-end justify-between gap-x-8 border-0 border-b '
          + 'border-solid border-gift-line max-md:hidden'}
      >
        {/* Real buttons, not links or divs: this changes what the page shows,
            and a keyboard user should get it from the tab order for free. */}
        {/* THE ONE THING ALLOWED TO SCROLL SIDEWAYS. Five tabs do not fit 390px,
            and wrapping to a second line would push the products off the first
            screen. `-mx-4 px-4` lets the row bleed into the page gutter so the
            last tab is not clipped mid-word against the edge. */}
        <div
          /* ⚠️ `-mb-px` AND `overflow-y-hidden` BELONG HERE, on the scroller.
             `overflow-x: auto` does not leave the other axis alone: CSS
             computes a `visible` overflow-y to `auto` whenever the other axis
             is not visible. With the -1px overlap on each BUTTON, every tab's
             border box hung 1px below the scroller's content box, so that
             implied auto found 1px of overflow and drew a VERTICAL scrollbar
             down the tab row.
             Pulling the whole strip down by 1px instead puts the active tab's
             3px underline over the row's rule exactly as before -- the overlap
             is between the strip and its parent, not inside the scroller -- and
             leaves nothing to overflow. overflow-y-hidden is the belt to that
             braces, against sub-pixel rounding at odd zoom levels. */
          className={cn(
            styles.tabStrip,
            '-mx-4 -mb-px flex min-w-0 flex-1 items-end gap-[22px] overflow-x-auto',
            'overflow-y-hidden px-4 md:mx-0 md:gap-8 md:px-0',
          )}
        >
          {[{ key: ALL, label: 'All' }, ...tabs].map((tab) => {
            const isActive = category === tab.key;

            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setCategory(tab.key)}
                aria-current={isActive ? 'true' : undefined}
                className={cn(
                  // The -1px overlap lives on the scrolling strip, not here --
                  // see the note on it. On the button it made every tab hang
                  // 1px out of the scroller and produced a vertical scrollbar.
                  'inline-flex h-[46px] shrink-0 items-center whitespace-nowrap border-0 border-b-[3px]',
                  'border-solid bg-transparent px-0 text-[15px] transition md:h-[52px] md:text-[16px]',
                  isActive
                    ? 'border-gift-ink font-bold text-gift-ink'
                    : 'border-transparent font-semibold text-gift-inkSoft hover:text-gift-ink',
                )}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* ⚠️ NOT ON A PHONE -- it gets its own row below this one instead.
            The tabs need the whole width to scroll in, and a select beside them
            left about 90px for five categories. Hidden rather than shrunk --
            `max-md:hidden`, never a bare `hidden`, which Bootstrap defines
            `!important` so nothing could un-hide it. */}
        <div
          data-testid={SORT_ROW_DESKTOP}
          className="flex shrink-0 items-center gap-2.5 pb-2.5 max-md:hidden"
        >
          <SortControl id="gift-sort" sortKey={sortKey} onChange={setSortKey} compact />
        </div>
      </div>

      {/* PHONE ONLY -- the whole toolbar on ONE line.

          Categories and sorting used to take two full rows above the products:
          a scrolling tab strip, then a sort row under it. On a 844px screen
          that is most of what a customer sees before any product. Both are
          <select>s here, side by side, the way a storefront's "Sort / Filter"
          row works (Alex, 2026-09-29, with a reference screenshot).

          ⚠️ SAME STATE AS THE TABS, not a copy: the category select reads
          `category` from the url and calls `setCategory`, exactly as the strip
          does, and the sort select is the same `SortControl` mounted again.

          `md:hidden` -- a responsive variant, so a DISTINCT class from
          Bootstrap's `.hidden { display: none !important }`. */}
      <div
        data-testid={SORT_ROW_PHONE}
        className="mt-6 flex items-center gap-2.5 md:hidden"
      >
        <CategorySelect tabs={tabs} category={category} onChange={setCategory} />
        <SortControl id="gift-sort-phone" sortKey={sortKey} onChange={setSortKey} />
      </div>

      <div className="mt-6 min-w-0">
        {visible.length === 0 ? (
        // Never a blank shelf with no explanation, or it reads as a page that
        // failed to load. The tabs are built FROM the data, so an empty one
        // should be unreachable -- this catches the case they cannot: a
        // `?category=` in a shared link or an old bookmark naming a section
        // this customer has no products in.
          <div className="rounded-gift-hero border border-gift-line p-10 text-center">
            <p className="text-gift-inkSoft">Nothing to show in this category.</p>
            <button
              type="button"
              onClick={() => setCategory(ALL)}
              className="mt-3 border-0 bg-transparent font-semibold text-gift-accent underline underline-offset-4"
            >
              Show all products
            </button>
          </div>
        ) : (
          <div
            id={PRODUCT_GRID_ID}
            /* EVERY product in the category, always -- no pagination, no
               load-more. Parents scroll, and a shelf that hides half its stock
               behind a button sells less of it.
               The row gap stays larger than the column gap at every width,
               which is what keeps a name that wraps to two lines off the tile
               below. */
            /* 2 up on a phone, 3 on a tablet, 4 from `lg`. THREE STARTS AT
               `md`, not `sm`: between 640 and 767 three tiles leave each mockup
               about 190px wide, narrower than the phone's own two-up. */
            /* ⚠️ THE GAPS ALSO SET THE THUMBNAIL SIZE. The column count is
               fixed and the tile image is `aspect-square w-full`, so every
               pixel added to a gutter comes off the artwork that shares the
               row -- the shelf breathing and the thumbnails shrinking are ONE
               number here, not two that can disagree.
               Widened 2026-09-29 (Alex, "slightly smaller, more space between
               them"): at 1440 the tiles go 307px -> 295px. */
            className={'grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-3 md:gap-x-6 '
              + 'md:gap-y-10 lg:grid-cols-4 lg:gap-x-11 lg:gap-y-14'}
          >
            {/* Two across on a phone, three on a tablet, four from `lg`. The
                  grid now owns the full content width -- there is no sidebar to
                  pay for at any breakpoint. */}
            {visible.map(({ addon }) => {
              const added = addedAddons.get(String(addon.id));

              return (
                <GiftTile
                  key={addon.name}
                  name={addon.name}
                  priceNode={(
                    <ItemSubtitle
                      price={addonUnitPrice(addon, orderPackage)}
                      addon={addon}
                      offer={offer}
                      qualifiesForFreeAnnouncements={qualifiesForFreeAnnouncements}
                      // The PromoBanner above the grid already says it -- once,
                      // rather than once per tile.
                      showOfferNote={false}
                    />
                  )}
                  /* The composited preview -- the customer's own portrait in the
                     product, which is the whole point of this shelf. Item owns
                     how each variant is drawn. Asked BEFORE rendering, because
                     an add-on with no product photo draws an EMPTY box rather
                     than nothing, and the tile's placeholder could never tell
                     the difference after the fact. */
                  preview={canRenderPreview(addon, addon.cssName) ? (
                    <Item
                      previewOnly
                      addon={addon}
                      variant={addon.cssName}
                      background={backgrounds[0]}
                      /* ⚠️ THE ELIGIBLE pose, not `poses[0]`. This tile draws
                         the real composite, so an ineligible first pose shows
                         the exact breakage the product screen now refuses --
                         a landscape photo overflowing the magnet's cutout, or a
                         JPG's baked-in backdrop as a rectangle of studio canvas
                         on a photo of a fridge. Same rule, every surface that
                         draws a pose on a product. */
                      pose={eligiblePoses(poses, addon, poseOrientations)[0]}
                    />
                  ) : null}
                  imageUrl={addon.imageUrl}
                  isAdded={!!added}
                  /* productKey, not cssName: the tile is one ROW, the screen
                     it opens is one PRODUCT, and a renderer can carry more than
                     one product (the neutral and school weekly magnets are both
                     `weeklycutout`). Defaults to cssName server-side. */
                  onCustomize={() => onSelect(productKeyOf(addon))}
                  onOpenAdded={() => onOpenAdded(added)}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

GiftShop.defaultProps = {
  poseOrientations: {},
  addedAddons: new Map(),
  onOpenAdded: () => {},
  inOrderFlow: false,
  orderType: ORDER_TYPES.UNKNOWN,
  poses: [],
  backgrounds: [],
  orderPackage: null,
  offer: null,
  qualifiesForFreeAnnouncements: false,
};

GiftShop.propTypes = {
  // Spraypaint Addon records, ALREADY filtered by audience (giftAddonVisible).
  // eslint-disable-next-line react/forbid-prop-types
  addons: PropTypes.array.isRequired,
  // (productKey) => void — opens that product's builder.
  onSelect: PropTypes.func.isRequired,
  // url -> { orientation }, from useImageOrientations in ItemAdder.
  // eslint-disable-next-line react/forbid-prop-types
  poseOrientations: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  poses: PropTypes.array,
  // eslint-disable-next-line react/forbid-prop-types
  backgrounds: PropTypes.array,
  // eslint-disable-next-line react/forbid-prop-types
  orderPackage: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  offer: PropTypes.object,
  qualifiesForFreeAnnouncements: PropTypes.bool,
  // True when the customer was sent here by the order flow (`?optional=`), as
  // opposed to following a direct or campaign link. Presentation only.
  inOrderFlow: PropTypes.bool,
  // From resolveOrderType — picks which hero config the banner draws.
  orderType: PropTypes.oneOf(Object.values(ORDER_TYPES)),
  // addonId (as a string) -> { orderPackageId, sheetIds, count }, from the real
  // cart rather than from anything this page remembers. See useCartAddonSheets.
  // eslint-disable-next-line react/forbid-prop-types
  addedAddons: PropTypes.object,
  // Opens an add-on the customer already has, so they can edit or remove it.
  onOpenAdded: PropTypes.func,
};
